import { useMemo } from 'react'
import {
  GeckoComponentModel,
  GeckoMarkupLayer,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Hook to calculate the ratio of area covered by scans versus the total area of the component
 *
 * @returns 0-1 ratio of area coverage.
 */
export const useScanAreaRatio = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): number =>
  useMemo(
    () =>
      computeScanAreaRatio(
        markupLayersByInspection,
        componentModelsByInspection
      ),
    [componentModelsByInspection, markupLayersByInspection]
  )

/**
 * Hook to calculate the total area covered by scans
 *
 * @returns The total area covered by scans
 */
export const useScanArea = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): { value: number; unit: GeckoMeasurementUnit } =>
  useMemo(
    () =>
      computeScanArea(markupLayersByInspection, componentModelsByInspection),
    [componentModelsByInspection, markupLayersByInspection]
  )

/**
 * Utility function to calculate the ratio of area covered by scans versus the total area of the component
 *
 * @param markupLayersByInspection - The markup layers by inspection to calculate the scan area ratio from
 * @param componentModelsByInspection - The component models by inspection to calculate the scan area ratio from
 * @returns 0-1 ratio of area coverage.
 */
export const computeScanAreaRatio = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): number => {
  const totalScanArea = computeScanArea(
    markupLayersByInspection,
    componentModelsByInspection
  )

  const totalComponentArea = computeTotalComponentArea(
    componentModelsByInspection
  )

  return totalComponentArea.value > 0
    ? totalScanArea.value / totalComponentArea.value
    : 0
}

/**
 * Utility function to calculate the area covered by scans across multiple inspections
 *
 * @param markupLayersByInspection - The markup layers by inspection to calculate the scan area from
 * @param componentModelsByInspection - The component models by inspection to calculate the scan area from
 * @returns The total area covered by scans with its unit
 */
export const computeScanArea = (
  markupLayersByInspection: ReadonlyMap<string, readonly GeckoMarkupLayer[]>,
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): { value: number; unit: GeckoMeasurementUnit } => {
  let totalScanArea = 0
  let unit: GeckoMeasurementUnit | undefined

  markupLayersByInspection.forEach((markupLayers, inspectionId) => {
    const selectedComponentModel = componentModelsByInspection.get(inspectionId)

    const scanOverlaysLayer = markupLayers.find(
      (layer) =>
        layer.data?.name === 'Scan Overlay' &&
        Array.isArray(layer.data?.path) &&
        layer.data?.path[0] === 'Report'
    )

    if (!(scanOverlaysLayer && selectedComponentModel)) return

    const cmUnit = selectedComponentModel.componentBounds.x.unit

    // We take the unit from the first valid component model
    if (!unit) {
      unit = cmUnit
    }

    const scanArea = scanOverlaysLayer.data.rectPrimitives
      .map((rect) => {
        const area =
          rect.bounds.x.size.toNumber(cmUnit) *
          rect.bounds.y.size.toNumber(cmUnit)
        return area
      })
      .reduce((prevNum, currNum) => prevNum + currNum, 0)

    totalScanArea += scanArea
  })

  return { value: totalScanArea, unit: unit ?? GeckoMeasurementUnit.None }
}

export const computeTotalComponentArea = (
  componentModelsByInspection: ReadonlyMap<string, GeckoComponentModel>
): { value: number; unit: GeckoMeasurementUnit } => {
  let totalArea = 0
  let unit: GeckoMeasurementUnit | undefined

  componentModelsByInspection.forEach((componentModel) => {
    const cmUnit = componentModel.componentBounds.x.unit

    // We take the unit from the first valid component model
    if (!unit) {
      unit = cmUnit
    }

    const cmArea =
      componentModel.componentBounds.x.size.toNumber(cmUnit) *
      componentModel.componentBounds.y.size.toNumber(cmUnit)
    totalArea += cmArea
  })

  return { value: totalArea, unit: unit ?? GeckoMeasurementUnit.None }
}
